import { useEffect, useState } from 'react';
import { Ticket, Plus, X, Trash2, Copy, CheckCircle, ChevronRight, ChevronLeft, Zap, Percent, Clock, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { sportsApi, useSportsAuth } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';
import KioskVoucherManager from '../kiosk/KioskVoucherManager';

const RED = '#E63946';
const GREEN = '#00E676';
const DOW = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['0', 'Sun']];

const TYPES = [
  { id: 'flat', label: 'Less per session', desc: '₱ off the whole booking', Icon: Wallet },
  { id: 'per_hour', label: 'Less per hour', desc: '₱ off each booked hour', Icon: Clock },
  { id: 'percent', label: 'Percent off', desc: '% off the total', Icon: Percent },
  { id: 'free_time', label: 'Free time', desc: 'Free extra minutes if booked N hours', Icon: Zap },
];

const inputStyle = {
  background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff',
  borderRadius: 8, padding: '10px 12px', width: '100%', outline: 'none',
  fontFamily: 'Manrope, sans-serif', fontSize: 14, boxSizing: 'border-box',
};

function todayISO() { return new Date().toISOString().slice(0, 10); }

function StatusBadge({ status }) {
  const map = {
    unused: { c: GREEN, t: 'Unused' }, used: { c: '#737373', t: 'Used' },
    expired: { c: '#FF2D55', t: 'Expired' }, scheduled: { c: '#FFB300', t: 'Scheduled' },
  };
  const s = map[status] || map.unused;
  return (
    <span data-testid={`voucher-status-${status}`} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
      color: s.c, background: s.c + '18', padding: '3px 8px', borderRadius: 999 }}>{s.t}</span>
  );
}

function condSummary(v) {
  const bits = [];
  if (v.min_hours) bits.push(`≥ ${(+v.min_hours)}h`);
  if (v.min_amount) bits.push(`≥ ₱${(+v.min_amount)}`);
  if (v.time_window?.start) bits.push(`${v.time_window.start}–${v.time_window.end}`);
  if (v.days?.length) bits.push(v.days.map(d => (DOW.find(x => +x[0] === +d) || [])[1]).join('/'));
  if (v.sports?.length) bits.push(v.sports.join('/'));
  return bits.join(' · ') || 'No conditions';
}

// ── K6 P4: the court's own BALL MACHINE vouchers (court-funded, its linked phones only) ──
// 2026-10-01: the voucher manager (same component as HighMark's page, mode="tenant"): its own batches with
// copy / extend / retire, plus HighMark's batches for its machine(s) read-only ("From HighMark").
function BallMachineVouchers({ courtName }) {
  return (
    <section style={{ marginTop: 40 }} data-testid="ball-machine-vouchers">
      <KioskVoucherManager mode="tenant" courtName={courtName} />
    </section>
  );
}

export default function SportsVouchersPage() {
  const { organization } = useSportsAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, unused: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [courts, setCourts] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);

  const sports = [...new Set(courts.map(c => c.sport))];

  const load = () => {
    setLoading(true);
    Promise.all([
      sportsApi.get('/sports/vouchers', { params: { channel: 'booking' } }),
      sportsApi.get('/sports/vouchers/stats', { params: { channel: 'booking' } }),
    ]).then(([r, s]) => { setRows(r.data || []); setStats(s.data || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    sportsApi.get('/sports/courts').then(r => setCourts((r.data || []).filter(c => c.active))).catch(() => {});
    load();
  }, []);

  const filtered = filter === 'all' ? rows : rows.filter(v => v.status === filter);

  const deleteVoucher = async (id) => {
    try { await sportsApi.delete(`/sports/vouchers/${id}`); toast.success('Voucher deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not delete'); }
  };

  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff' }}>
      <SportsNavBar />
      <main className="max-w-7xl mx-auto px-5 py-8" data-testid="sports-vouchers-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: RED + '18', color: RED }}>
              <Ticket className="h-5 w-5" />
            </span>
            <div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Vouchers</h1>
              <p style={{ fontSize: 12, color: '#737373' }}>Mint single-use promo codes — discounts & free-time bonuses</p>
            </div>
          </div>
          <button data-testid="voucher-create-btn" onClick={() => setWizardOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: RED, color: '#fff', border: 'none',
              borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer', boxShadow: '0 0 20px rgba(230,57,70,0.4)' }}>
            <Plus className="h-4 w-4" /> Create Vouchers
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[['Total', stats.total, '#fff'], ['Unused', stats.unused, GREEN], ['Used', stats.used, '#737373'], ['Expired', stats.expired, '#FF2D55']].map(([label, val, color]) => (
            <div key={label} style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '14px 16px' }}>
              <p style={{ fontSize: 11, color: '#737373', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</p>
              <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 26, fontWeight: 900, color }} data-testid={`voucher-stat-${label.toLowerCase()}`}>{val ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {['all', 'unused', 'used', 'expired'].map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`voucher-filter-${f}`}
              style={{ fontSize: 12, fontWeight: 700, textTransform: 'capitalize', padding: '7px 14px', borderRadius: 999,
                border: `1px solid ${filter === f ? RED : '#2A2A2A'}`, background: filter === f ? RED + '18' : 'transparent',
                color: filter === f ? RED : '#737373', cursor: 'pointer' }}>{f}</button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <p style={{ color: '#737373', textAlign: 'center', padding: 40 }}>Loading vouchers…</p>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, background: '#141414', borderRadius: 12, border: '1px solid #1E1E1E' }}>
            <Ticket className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
            <p style={{ color: '#737373', fontSize: 14 }}>No vouchers yet. Create a batch to get started.</p>
          </div>
        ) : (
          <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, overflow: 'hidden' }}>
            {filtered.map(v => (
              <div key={v.id} data-testid={`voucher-row-${v.code}`}
                className="flex items-center justify-between gap-3 flex-wrap"
                style={{ padding: '14px 16px', borderBottom: '1px solid #1E1E1E' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => copyCode(v.code)} data-testid={`voucher-copy-${v.code}`}
                      style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 800, color: '#fff', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 5 }}>
                      {v.code} <Copy className="h-3 w-3" style={{ color: '#555' }} />
                    </button>
                    <StatusBadge status={v.status} />
                  </div>
                  <p style={{ fontSize: 12, color: v.type === 'free_time' ? GREEN : RED, fontWeight: 700, marginTop: 3 }}>{v.label}</p>
                  <p style={{ fontSize: 11, color: '#737373', marginTop: 1 }}>
                    {condSummary(v)} · exp {v.valid_until}{v.used ? ` · used by ${v.used_customer || '—'}` : ''}
                  </p>
                </div>
                {v.status !== 'used' && (
                  <button onClick={() => deleteVoucher(v.id)} data-testid={`voucher-delete-${v.code}`}
                    style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: 8, color: '#737373', cursor: 'pointer' }}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {organization?.has_kiosk && <BallMachineVouchers courtName={organization?.name || ''} />}
      </main>

      {wizardOpen && (
        <VoucherWizard courts={courts} sports={sports}
          onClose={() => setWizardOpen(false)}
          onDone={() => { setWizardOpen(false); load(); }} />
      )}
    </div>
  );
}

function VoucherWizard({ courts, sports, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [f, setF] = useState({
    type: 'flat', value: '', min_hours: '', min_amount: '',
    sports: [], court_ids: [], useWindow: false, win_start: '18:00', win_end: '22:00', days: [],
    quantity: 20, prefix: 'PLAY', code_length: 5,
    valid_from: todayISO(), valid_until: '',
  });
  const set = (k) => (v) => setF(p => ({ ...p, [k]: v }));
  const toggleArr = (k, val) => setF(p => ({ ...p, [k]: p[k].includes(val) ? p[k].filter(x => x !== val) : [...p[k], val] }));

  const valueLabel = f.type === 'percent' ? 'Percent off (%)' : f.type === 'free_time' ? 'Free minutes' : 'Amount off (₱)';
  const typeMeta = TYPES.find(t => t.id === f.type);

  const canNext1 = f.type && Number(f.value) > 0;
  const canGenerate = f.quantity >= 1 && f.prefix.trim() && f.valid_until;

  const generate = async () => {
    setSaving(true);
    try {
      const payload = {
        type: f.type, value: Number(f.value),
        min_hours: f.min_hours !== '' ? Number(f.min_hours) : null,
        min_amount: f.min_amount !== '' ? Number(f.min_amount) : null,
        sports: f.sports, court_ids: f.court_ids,
        time_window: f.useWindow ? { start: f.win_start, end: f.win_end } : null,
        days: f.days.map(Number),
        quantity: Number(f.quantity), prefix: f.prefix.trim().toUpperCase(),
        code_length: Number(f.code_length),
        valid_from: f.valid_from, valid_until: f.valid_until,
      };
      const r = await sportsApi.post('/sports/vouchers/generate', payload);
      setResult(r.data);
      if (r.data.count < Number(f.quantity)) {
        toast.warning(`Generated ${r.data.count} of ${f.quantity} (some codes collided). You can mint more.`);
      } else {
        toast.success(`Generated ${r.data.count} vouchers`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setSaving(false); }
  };

  const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} data-testid="voucher-wizard">
      <div style={card}>
        {/* Header */}
        <div className="flex items-center justify-between" style={{ padding: '18px 20px', borderBottom: '1px solid #1E1E1E' }}>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 800, textTransform: 'uppercase' }}>
            {result ? 'Vouchers Ready' : `Create Vouchers · Step ${step}/3`}
          </h2>
          <button onClick={onClose} data-testid="voucher-wizard-close" style={{ background: 'none', border: 'none', color: '#737373', cursor: 'pointer' }}><X className="h-5 w-5" /></button>
        </div>

        <div style={{ padding: 20 }}>
          {/* ── Success screen ── */}
          {result ? (
            <div>
              <div className="flex items-center gap-2 mb-3" style={{ color: GREEN }}>
                <CheckCircle className="h-5 w-5" /> <span style={{ fontWeight: 700 }}>{result.count} unique codes minted</span>
              </div>
              <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12, maxHeight: 220, overflowY: 'auto' }}>
                {result.codes.map(c => (
                  <div key={c} className="flex items-center justify-between" style={{ padding: '4px 0' }}>
                    <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, letterSpacing: 0.5 }}>{c}</span>
                    <button onClick={() => { navigator.clipboard?.writeText(c); toast.success('Copied'); }} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer' }}><Copy className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <button onClick={onDone} data-testid="voucher-wizard-finish"
                style={{ marginTop: 16, width: '100%', background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 12, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>Done</button>
            </div>
          ) : (
            <>
              {/* ── Step 1: Type & Value ── */}
              {step === 1 && (
                <div>
                  <p style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1, marginBottom: 10 }}>Voucher type</p>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {TYPES.map(t => (
                      <button key={t.id} onClick={() => set('type')(t.id)} data-testid={`voucher-type-${t.id}`}
                        style={{ textAlign: 'left', padding: 12, borderRadius: 12, cursor: 'pointer',
                          border: `1.5px solid ${f.type === t.id ? (t.id === 'free_time' ? GREEN : RED) : '#2A2A2A'}`,
                          background: f.type === t.id ? (t.id === 'free_time' ? GREEN : RED) + '14' : '#0A0A0A' }}>
                        <t.Icon className="h-4 w-4 mb-1.5" style={{ color: f.type === t.id ? (t.id === 'free_time' ? GREEN : RED) : '#737373' }} />
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{t.label}</p>
                        <p style={{ fontSize: 10, color: '#737373', marginTop: 2 }}>{t.desc}</p>
                      </button>
                    ))}
                  </div>
                  <label style={{ display: 'block' }}>
                    <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>{valueLabel}</span>
                    <input data-testid="voucher-value" type="number" value={f.value} onChange={e => set('value')(e.target.value)}
                      placeholder={f.type === 'percent' ? 'e.g. 15' : f.type === 'free_time' ? 'e.g. 30' : 'e.g. 50'}
                      style={{ ...inputStyle, marginTop: 6 }} />
                  </label>
                </div>
              )}

              {/* ── Step 2: Conditions & Scope ── */}
              {step === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Min hours booked{f.type === 'free_time' ? ' *' : ''}</span>
                      <input data-testid="voucher-min-hours" type="number" step="0.5" value={f.min_hours} onChange={e => set('min_hours')(e.target.value)} placeholder="e.g. 2" style={{ ...inputStyle, marginTop: 6 }} />
                    </label>
                    <label>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Min amount (₱)</span>
                      <input data-testid="voucher-min-amount" type="number" value={f.min_amount} onChange={e => set('min_amount')(e.target.value)} placeholder="optional" style={{ ...inputStyle, marginTop: 6 }} />
                    </label>
                  </div>

                  <div>
                    <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Applicable sports (blank = all)</span>
                    <div className="flex gap-2 flex-wrap mt-2">
                      {sports.map(s => (
                        <button key={s} onClick={() => toggleArr('sports', s)}
                          style={{ fontSize: 12, padding: '5px 12px', borderRadius: 999, cursor: 'pointer', textTransform: 'capitalize',
                            border: `1px solid ${f.sports.includes(s) ? RED : '#2A2A2A'}`, background: f.sports.includes(s) ? RED + '18' : 'transparent', color: f.sports.includes(s) ? RED : '#a3a3a3' }}>{s}</button>
                      ))}
                      {sports.length === 0 && <span style={{ fontSize: 12, color: '#555' }}>No courts yet</span>}
                    </div>
                  </div>

                  <div>
                    <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Valid days (blank = all)</span>
                    <div className="flex gap-1.5 flex-wrap mt-2">
                      {DOW.map(([val, lbl]) => (
                        <button key={val} onClick={() => toggleArr('days', val)}
                          style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
                            border: `1px solid ${f.days.includes(val) ? RED : '#2A2A2A'}`, background: f.days.includes(val) ? RED + '18' : 'transparent', color: f.days.includes(val) ? RED : '#a3a3a3' }}>{lbl}</button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={f.useWindow} onChange={e => set('useWindow')(e.target.checked)} data-testid="voucher-use-window" />
                      <span style={{ fontSize: 13, color: '#fff' }}>Off-peak time window only</span>
                    </label>
                    {f.useWindow && (
                      <div className="flex gap-3 mt-2">
                        <input type="time" value={f.win_start} onChange={e => set('win_start')(e.target.value)} style={inputStyle} />
                        <input type="time" value={f.win_end} onChange={e => set('win_end')(e.target.value)} style={inputStyle} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── Step 3: Batch & Review ── */}
              {step === 3 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>How many? *</span>
                      <input data-testid="voucher-quantity" type="number" min="1" max="500" value={f.quantity} onChange={e => set('quantity')(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                    </label>
                    <label>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Code length</span>
                      <select data-testid="voucher-code-length" value={f.code_length} onChange={e => set('code_length')(e.target.value)} style={{ ...inputStyle, marginTop: 6, cursor: 'pointer' }}>
                        <option value={4}>4 characters</option>
                        <option value={5}>5 characters</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Prefix *</span>
                    <input data-testid="voucher-prefix" value={f.prefix} onChange={e => set('prefix')(e.target.value.toUpperCase())} placeholder="PLAY30" style={{ ...inputStyle, marginTop: 6 }} />
                    <span style={{ fontSize: 11, color: '#555' }}>Codes look like <b style={{ color: '#a3a3a3' }}>{(f.prefix || 'VCH').toUpperCase()}-{'X'.repeat(Number(f.code_length))}</b></span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Valid from</span>
                      <input data-testid="voucher-valid-from" type="date" value={f.valid_from} onChange={e => set('valid_from')(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                    </label>
                    <label>
                      <span style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1 }}>Expiry *</span>
                      <input data-testid="voucher-valid-until" type="date" value={f.valid_until} min={f.valid_from} onChange={e => set('valid_until')(e.target.value)} style={{ ...inputStyle, marginTop: 6 }} />
                    </label>
                  </div>
                  <div style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 12 }}>
                    <p style={{ fontSize: 11, textTransform: 'uppercase', color: '#737373', letterSpacing: 1, marginBottom: 6 }}>Review</p>
                    <p style={{ fontSize: 13, color: '#fff' }}>{typeMeta?.label} · <b style={{ color: f.type === 'free_time' ? GREEN : RED }}>{f.value}{f.type === 'percent' ? '%' : f.type === 'free_time' ? ' min free' : '₱'}</b></p>
                    <p style={{ fontSize: 12, color: '#737373', marginTop: 3 }}>
                      {f.min_hours ? `Book ≥ ${f.min_hours}h · ` : ''}{f.min_amount ? `Min ₱${f.min_amount} · ` : ''}
                      {f.quantity} codes · expires {f.valid_until || '—'}
                    </p>
                  </div>
                </div>
              )}

              {/* Footer nav */}
              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <button onClick={() => setStep(step - 1)} data-testid="voucher-wizard-back"
                    style={{ flex: 1, background: 'transparent', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 10, padding: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <ChevronLeft className="h-4 w-4" /> Back
                  </button>
                )}
                {step < 3 ? (
                  <button onClick={() => setStep(step + 1)} disabled={step === 1 && !canNext1} data-testid="voucher-wizard-next"
                    style={{ flex: 2, background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: 11, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: (step === 1 && !canNext1) ? 'not-allowed' : 'pointer', opacity: (step === 1 && !canNext1) ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button onClick={generate} disabled={!canGenerate || saving} data-testid="voucher-generate-btn"
                    style={{ flex: 2, background: GREEN, color: '#000', border: 'none', borderRadius: 10, padding: 11, fontWeight: 800, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', cursor: (!canGenerate || saving) ? 'not-allowed' : 'pointer', opacity: (!canGenerate || saving) ? 0.5 : 1 }}>
                    {saving ? 'Generating…' : 'Generate Vouchers'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
